import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import useQueryParam from '@jwp/ott-ui-react/src/hooks/useQueryParam';
import usePlaylist from '@jwp/ott-hooks-react/src/usePlaylist';
import type { PlaylistItem } from '@jwp/ott-common/types/playlist';
import ArrowLeft from '@jwp/ott-theme/assets/icons/arrow_left.svg?react';
import ArrowUp from '@jwp/ott-theme/assets/icons/Arrow_up.svg?react';
import ArrowDown from '@jwp/ott-theme/assets/icons/Arrow_down.svg?react';
import AudioOn from '@jwp/ott-theme/assets/icons/audio-on.svg?react';
import AudioOff from '@jwp/ott-theme/assets/icons/audio-off.svg?react';
import PlayIcon from '@jwp/ott-theme/assets/icons/play.svg?react';

import { modalURLFromLocation } from '../../utils/location';
import type { JWPlayer } from '../../../types/jwplayer';
import Player from '../../components/Player/Player';
import Icon from '../Icon/Icon';
import IconButton from '../IconButton/IconButton';
import LoadingOverlay from '../LoadingOverlay/LoadingOverlay';
import CollapsibleText from '../CollapsibleText/CollapsibleText';
import { handleBackNavigate } from '../../utils/handleBackNavigate';

import styles from './VerticalPlayer.module.scss';

type Props = {
  playlist?: PlaylistItem[];
};



const VerticalPlayer: React.FC<Props> = ({ playlist: propsPlaylist }) => {
  const { t } = useTranslation('video');
  const navigate = useNavigate();
  const location = useLocation();
  const { id: mediaId } = useParams();
  const playlistId = useQueryParam('r');
  const modalView = useQueryParam('u');

  const { data: fetchedPlaylist, isLoading } = usePlaylist(playlistId || undefined);

  const playlist = useMemo(() => propsPlaylist || fetchedPlaylist?.playlist || [], [propsPlaylist, fetchedPlaylist?.playlist]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const [playerInstance, setPlayerInstance] = useState<JWPlayer | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [showVolumeBar, setShowVolumeBar] = useState(false);

  useEffect(() => {
    if (playlist.length > 0 && mediaId) {
      const index = playlist.findIndex((item) => item.mediaid === mediaId);
      if (index !== -1) {
        setCurrentIndex(index);
      }
    }
  }, [playlist, mediaId]);

  const handleNext = useCallback(() => {
    setCurrentIndex((prev) => (prev < playlist.length - 1 ? prev + 1 : prev));
  }, [playlist.length]);

  const handlePrev = useCallback(() => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : prev));
  }, []);

  const handleComplete = useCallback(() => {
    playerInstance?.seek(0);
    playerInstance?.play();
  }, [playerInstance]);

  const handleReady = useCallback((player?: JWPlayer) => {
    if (player) {
      setPlayerInstance(player);
      setDuration(player.getDuration() || 0);
      setIsMuted(player.getMute());
      
      player.on('time', (event) => {
        setCurrentTime(event.position);
        setDuration(event.duration);
      });

      player.on('play', () => setIsPaused(false));
      player.on('pause', () => setIsPaused(true));
      player.on('mute', (event) => setIsMuted(event.mute));
    }
  }, [setCurrentTime, setDuration]);

  const togglePlayPause = useCallback(() => {
    if (!playerInstance) return;
    
    const state = playerInstance.getState();
    if (state === 'playing') {
      playerInstance.pause();
    } else {
      playerInstance.play();
    }
  }, [playerInstance]);

  const toggleMute = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!playerInstance) return;
    const newMuted = !isMuted;
    playerInstance.setMute(newMuted);
    setIsMuted(newMuted);
    if (!newMuted && volume === 0) {
      setVolume(1);
      playerInstance.setVolume(100);
    }
  }, [playerInstance, isMuted, volume]);

  const handleVolumeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = Number.parseFloat(e.target.value);
    setVolume(val);
    if (playerInstance) {
      playerInstance.setVolume(val * 100);
      if (val > 0 && isMuted) {
        playerInstance.setMute(false);
        setIsMuted(false);
      } else if (val === 0 && !isMuted) {
        playerInstance.setMute(true);
        setIsMuted(true);
      }
    }
  }, [playerInstance, isMuted]);

  const handleSeek = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (!playerInstance || duration <= 0) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / rect.width;
    playerInstance.seek(pos * duration);
  }, [playerInstance, duration]);

  const touchStartRef = useRef<number | null>(null);
  const scrollLockRef = useRef<boolean>(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const handleTouchStart = (e: TouchEvent) => {
      touchStartRef.current = e.touches[0].clientY;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (touchStartRef.current === null || modalView) return;
      const touchMove = e.touches[0].clientY;
      const diff = touchStartRef.current - touchMove;
      if (Math.abs(diff) > 5) {
        if (e.cancelable) e.preventDefault();
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (touchStartRef.current === null || modalView) return;
      const touchEnd = e.changedTouches[0].clientY;
      const diff = touchStartRef.current - touchEnd;
      if (Math.abs(diff) > 50) {
        if (diff > 0) {
          handleNext();
        } else {
          handlePrev();
        }
      }
      touchStartRef.current = null;
    };

    wrapper.addEventListener('touchstart', handleTouchStart, { passive: true });
    wrapper.addEventListener('touchmove', handleTouchMove, { passive: false });
    wrapper.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      wrapper.removeEventListener('touchstart', handleTouchStart);
      wrapper.removeEventListener('touchmove', handleTouchMove);
      wrapper.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleNext, handlePrev, modalView]);

  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (scrollLockRef.current || modalView) return;
      if (Math.abs(e.deltaY) > 30) {
        if (e.deltaY > 0) {
          handleNext();
        } else {
          handlePrev();
        }
        scrollLockRef.current = true;
        setTimeout(() => {
          scrollLockRef.current = false;
        }, 800);
      }
    };
    window.addEventListener('wheel', handleWheel, { passive: true });
    return () => window.removeEventListener('wheel', handleWheel);
  }, [handleNext, handlePrev, modalView]);

  useEffect(() => {
    if (playlist[currentIndex] && playlist[currentIndex].mediaid !== mediaId) {
      const currentItem = playlist[currentIndex];
      const newPath = window.location.pathname.replace(mediaId || '', currentItem.mediaid);
      window.history.replaceState(null, '', `${newPath}${window.location.search}`);
    }
  }, [currentIndex, playlist, mediaId]);

  useEffect(() => {
    if (modalView && playerInstance && !isPaused) {
      playerInstance.pause();
    }
  }, [modalView, playerInstance, isPaused]);

  const handleShowMore = () => {
    const currentVideo = playlist[currentIndex];
    if (currentVideo) {
      const metadataString = ''; // Vertical player doesn't have primary metadata in the same way
      const newState = { item: currentVideo, primaryMetadata: metadataString };
      sessionStorage.setItem('seeMoreDetails', JSON.stringify(newState));

      navigate(modalURLFromLocation(location, 'show-more-details'), {
        state: newState,
      });
    }
  };

  if (isLoading) {
    return <LoadingOverlay />;
  }

  if (!playlist || playlist.length === 0) {
    return <div className={styles.empty}>No videos available.</div>;
  }

  const currentVideo = playlist[currentIndex];
  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div ref={wrapperRef} className={classNames(styles.wrapper, { [styles.disabled]: !!modalView })}>
      <div className={styles.header}>
        <IconButton aria-label="Go Back" onClick={() => handleBackNavigate(navigate)} className={styles.backBtn}>
          <Icon icon={ArrowLeft} />
        </IconButton>
        <div className={styles.metaInfo}>
          <h1 className={styles.videoTitle}>{currentVideo?.title}</h1>
          <CollapsibleText
            item={currentVideo}
            text={currentVideo?.description}
            className={styles.videoDescription}
            maxWords={20}
            showMoreClickHandler={handleShowMore}
          />
          {!!currentVideo?.category && (
            <span className={classNames(styles.categoryBadge, styles.mobileCategory)}>{currentVideo?.category || currentVideo?.genre}</span>
          )}
          {/* <div className={styles.authorBadge}>
              <img src={(currentVideo?.authorImage as string) || 'https://ui-avatars.com/api/?name=User'} alt="Author" className={styles.avatar} />
              <span className={styles.authorName}>{(currentVideo?.author as string) || 'Hudson Joe'}</span>
            </div> */}
        </div>
      </div>

      <div className={styles.centerPanel}>
        <div className={styles.verticalContainer}>
          <div className={styles.playerWrapper} style={{ pointerEvents: 'none' }}>
            <Player
              key={currentVideo.mediaid}
              item={currentVideo}
              onComplete={handleComplete}
              onReady={handleReady}
              autostart={true}
              mute={false}
              ReConnecting={t('reconnecting', { defaultValue: 'Reconnecting...' })}
            />
          </div>
          <div className={styles.gestureOverlay} onClick={togglePlayPause} />

          <div className={styles.topLeftControls}>
            {!!currentVideo?.category && <span className={styles.categoryBadge}>{currentVideo?.category || currentVideo?.genre}</span>}
          </div>

          <div
            className={styles.volumeControlWrapper}
            onMouseEnter={() => setShowVolumeBar(true)}
            onMouseLeave={() => setShowVolumeBar(false)}
            onClick={(e) => e.stopPropagation()}
          >
            {showVolumeBar && (
              <div className={styles.volumeBarContainer} onClick={(e) => e.stopPropagation()}>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={isMuted ? 0 : volume}
                  onChange={handleVolumeChange}
                  className={styles.volumeSlider}
                  style={{ '--percent': `${(isMuted ? 0 : volume) * 100}%` } as React.CSSProperties}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            )}
            <IconButton className={styles.muteBtn} onClick={toggleMute}>
              <Icon icon={isMuted || volume === 0 ? AudioOff : AudioOn} />
            </IconButton>
          </div>

          {isPaused && (
            <div className={styles.playPauseOverlay}>
              <Icon icon={PlayIcon} />
            </div>
          )}

          <div className={styles.progressContainer} onClick={handleSeek}>
            <div className={styles.progressBarRail}>
              <div className={styles.progressBar} style={{ width: `${progressPercent}%` }} />
            </div>
          </div>
        </div>
      </div>

      <div className={styles.rightPanel}>
        <div className={styles.navigationArrows}>
          <IconButton
            className={classNames(styles.backBtn, styles.arrowBtn, { [styles.disabled]: currentIndex === 0 })}
            onClick={handlePrev}
            disabled={currentIndex === 0}
            aria-label="Previous Video"
          >
            <Icon icon={ArrowUp} />
          </IconButton>
          <IconButton
            className={classNames(styles.backBtn, styles.arrowBtn, { [styles.disabled]: currentIndex === playlist.length - 1 })}
            onClick={handleNext}
            disabled={currentIndex === playlist.length - 1}
            aria-label="Next Video"
          >
            <Icon icon={ArrowDown} />
          </IconButton>
        </div>
      </div>
    </div>
  );
};

export default VerticalPlayer;
